import { useState } from "react";
import { Column } from "primereact/column";
import { DataTable, DataTableSortMeta } from "primereact/datatable";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { formatNumber, formatNumberOneDecimal } from "../../shared/reportUtil";
import { AdminReportsResponse } from "../adminReportsApiSlice";
import { HolisticAndTraitScoresByGenreRow } from "./adminPerformanceReportApiSlice";

export function HolisticAndTraitScoresByGenreTable(props: {
	className: string;
	data: AdminReportsResponse<HolisticAndTraitScoresByGenreRow>;
}) {
	const tooltipOptions: TooltipOptions = {
		position: "top",
	};

	const [sortMeta, setSortMeta] = useState<DataTableSortMeta[]>([
		{ field: "courseName", order: 1 },
		{ field: "genreName", order: 1 },
	]);

	const headerRowTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return (
			<div className="flex items-center gap-2">
				<span className="font-semibold">{rowData.courseName}</span>
				<span className="text-gray-600 text-sm">{rowData.teachers}</span>
			</div>
		);
	};

	const genreTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return <span className="break-all">{rowData.genreName}</span>;
	};

	const developmentTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageDevelopment);
	};

	const organizationTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageOrganization);
	};

	const styleTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageStyle);
	};

	const wordChoiceTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageWordChoice);
	};

	const sentenceStructureTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageSentenceStructure);
	};

	const conventionsTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageConventions);
	};

	const totalTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumberOneDecimal(rowData.averageTotalScore);
	};

	const percentileTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumber(rowData.averagePercentileRank);
	};

	const stanineTemplate = (rowData: HolisticAndTraitScoresByGenreRow) => {
		return formatNumber(rowData.stanine);
	};

	const onSortHandler = (e: any) => {
		// Find what column the user clicked on
		const columnToSort = e.multiSortMeta[e.multiSortMeta.length - 1];

		if (columnToSort) {
			setSortMeta([
				{ field: "courseName", order: sortMeta[0].order }, // Keep this column locked
				{ field: columnToSort.field, order: columnToSort.order }, // Apply new sort within the groups
			]);
		}
	};

	return (
		<DataTable
			value={props.data.rows}
			rowGroupMode="subheader"
			groupRowsBy="courseId"
			rowGroupHeaderTemplate={headerRowTemplate}
			loading={props.data.rows == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			sortMode="multiple"
			multiSortMeta={sortMeta}
			onSort={onSortHandler}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}>
			<Column header="Course" />
			<Column
				header="Genre"
				body={genreTemplate}
				className="xl:!pl-24 2xl:!pl-36 !pr-2"
				headerClassName="xl:!pl-24 2xl:!pl-36 !pr-2"
				sortable
				sortField="genreName"></Column>
			<Column
				header="Student Count"
				field="studentCount"
				headerClassName="!px-2"
				className="!px-2"
				sortable></Column>
			<Column
				header="Dev"
				headerTooltip="Development of Ideas"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={developmentTemplate}
				sortable
				sortField="averageDevelopment"></Column>
			<Column
				header="Org"
				headerTooltip="Organization"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={organizationTemplate}
				sortable
				sortField="averageOrganization"></Column>
			<Column
				header="Style"
				headerTooltip="Style"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={styleTemplate}
				sortable
				sortField="averageStyle"></Column>
			<Column
				header="WC"
				headerTooltip="Word Choice"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={wordChoiceTemplate}
				sortable
				sortField="averageWordChoice"></Column>
			<Column
				header="Sent"
				headerTooltip="Sentence Fluency"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={sentenceStructureTemplate}
				sortable
				sortField="averageSentenceStructure"></Column>
			<Column
				header="Conv"
				headerTooltip="Conventions"
				headerTooltipOptions={tooltipOptions}
				headerClassName="!px-2"
				className="!px-2"
				body={conventionsTemplate}
				sortable
				sortField="averageConventions"></Column>
			<Column
				header="Total"
				headerClassName="!px-2"
				className="!px-2"
				body={totalTemplate}
				sortable
				sortField="averageTotalScore"></Column>
			<Column
				header="Percentile Rank"
				headerClassName="!px-2"
				className="!px-2"
				body={percentileTemplate}
				sortable
				sortField="averagePercentileRank"></Column>
			<Column
				header="Stanine"
				headerClassName="!px-2"
				className="!px-2"
				body={stanineTemplate}
				sortable
				sortField="stanine"></Column>
		</DataTable>
	);
}
